/**
 * Realtime action allowlist → Syntry router locations (spec §6), plus the
 * `resource`-based deep links the server's five-route allowlist cannot
 * express.
 *
 * An envelope's `action` is the only thing that steers navigation, and the
 * parser has already reduced it to one of {@link REALTIME_ACTION_ROUTES} (or
 * `null`). This module turns that closed set into the concrete routes declared
 * in `src/router/index.ts`, and answers "where should a tap go?" for toasts
 * and host notifications alike.
 *
 * Two layers, in order:
 *  1. {@link resolveRealtimeRoute} — the action allowlist verbatim.
 *  2. {@link resolveRealtimeTarget} — when the action is the server's generic
 *     fallback (`notifications.view` / `notifications.inbox`), the envelope's
 *     `resource {type,id}` picks the module page instead. The server stamps
 *     `resource` from the notification's context columns for every area
 *     (leave, attendance, loan, ticket, task, asset, …), so HR, Compensation,
 *     Productivity and Helpdesk events land on their own page without the
 *     wire contract growing a route per module.
 *
 * Every branch degrades to the inbox: a known route whose required parameter
 * is missing or empty must still take the user somewhere sensible, never throw
 * or navigate to `/tabs/messages/undefined`. A non-numeric id degrades to the
 * module's LIST page — the detail routes fetch by id and would only 404.
 */
import type { RouteLocationRaw } from 'vue-router';
import type { RealtimeAction, RealtimeEnvelope, RealtimeResource } from '@/contracts/realtime-envelope-v1';

/** The safe default: the notifications inbox tab. */
export const INBOX_LOCATION: RouteLocationRaw = { name: 'inbox' };

const DECIMAL_ID_RE = /^[0-9]{1,20}$/;

/** Read a non-empty string param, or `null`. */
function param(action: RealtimeAction, key: string): string | null {
  const value = action.params[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** A decimal record id, or `null` when the value cannot address a record. */
function recordId(value: string | null | undefined): string | null {
  return typeof value === 'string' && DECIMAL_ID_RE.test(value.trim()) ? value.trim() : null;
}

/**
 * Resolve an (already allowlisted) action to a router location.
 *
 * `resource` is optional and only consulted for a DIRECT message thread: the
 * wire's `thread_id` there is the counterpart USER id, while Syntry's
 * conversation route wants a MESSAGE id (the thread root, or any reply the
 * server resolves to it). `action.params.message_id` carries one; the
 * envelope's `resource` (`{type:'message', id}`) is the fallback.
 */
export function resolveRealtimeRoute(
  action: RealtimeAction | null,
  resource: RealtimeResource | null = null,
): RouteLocationRaw {
  if (action === null) return INBOX_LOCATION;

  switch (action.route) {
    case 'notifications.inbox':
      return INBOX_LOCATION;

    case 'notifications.view': {
      // The inbox opens the detail sheet for this id once its list has loaded.
      const id = param(action, 'notification_id');
      return id ? { name: 'inbox', query: { id } } : INBOX_LOCATION;
    }

    case 'messages.inbox':
      return { name: 'messages' };

    case 'messages.thread': {
      const threadId = param(action, 'thread_id');
      if (!threadId) return { name: 'messages' };
      // Group ids are namespaced `g-…` inside the app, but the wire contract
      // carries the raw id plus `thread_type`; route by type so the group page
      // receives the bare id it expects.
      const type = param(action, 'thread_type');
      if (type === 'group') return { name: 'group-conversation', params: { id: threadId } };
      if (type === 'direct') {
        const messageId =
          recordId(param(action, 'message_id')) ??
          (resource && resource.type === 'message' ? recordId(resource.id) : null);
        // Without a message id there is no way to name the conversation — the
        // user id in `thread_id` is not one — so land on the list.
        return messageId ? { name: 'conversation', params: { id: messageId } } : { name: 'messages' };
      }
      return { name: 'messages' };
    }

    case 'advisories.view': {
      const id = recordId(param(action, 'advisory_id'));
      return id ? { name: 'advisory-detail', params: { id } } : { name: 'announcements' };
    }

    default: {
      // Exhaustiveness guard: adding a route to the allowlist without a
      // mapping here is a compile error, not a silent inbox fallback.
      const unreachable: never = action.route;
      return unreachable;
    }
  }
}

/**
 * `resource.type` → the module page, keyed by the server's
 * `RealtimeResourceMap` type tokens. A `null` entry value means "known type,
 * no page in Syntry" (render as a plain inbox item); an absent key is the same
 * as unknown. Types whose page takes an id fall back to their list page when
 * the id is not a decimal record id.
 */
const RESOURCE_ROUTES: Readonly<Record<string, (id: string | null) => RouteLocationRaw>> = {
  // ── HR ──
  leave_application: () => ({ name: 'leaves' }),
  attendance: () => ({ name: 'attendance-list' }),
  schedule_change_request: () => ({ name: 'schedules' }),
  disciplinary: () => ({ name: 'disciplinary' }),
  // ── Compensation ──
  loan: (id) => (id ? { name: 'loan-detail', params: { id } } : { name: 'loans' }),
  // ── Productivity ──
  todo: () => ({ name: 'todo' }),
  calendar_event: (id) => (id ? { name: 'event-detail', params: { id } } : { name: 'events' }),
  timeline_post: () => ({ name: 'timeline' }),
  project: (id) => (id ? { name: 'project-detail', params: { id } } : { name: 'projects' }),
  task: (id) => (id ? { name: 'project-task-detail', params: { id } } : { name: 'project-my-tasks' }),
  // ── Helpdesk ──
  ticket: (id) => (id ? { name: 'ticketing-detail', params: { id } } : { name: 'ticketing' }),
  // ── Assets ──
  asset: (id) => (id ? { name: 'asset-detail', params: { id } } : { name: 'my-assets' }),
  asset_endorsement: (id) =>
    id ? { name: 'asset-endorsement-detail', params: { id } } : { name: 'asset-endorsements' },
  // ── Advisories / messages (normally reached through their own action) ──
  announcement: (id) => (id ? { name: 'advisory-detail', params: { id } } : { name: 'announcements' }),
  message: (id) => (id ? { name: 'conversation', params: { id } } : { name: 'messages' }),
};

/** The module page for a `resource`, or `null` when Syntry has no page for its type. */
export function resolveResourceRoute(resource: RealtimeResource | null): RouteLocationRaw | null {
  if (!resource) return null;
  const build = RESOURCE_ROUTES[resource.type];
  return build ? build(recordId(resource.id)) : null;
}

/**
 * Where a tap on this envelope should go.
 *
 * A specific action (a message thread, an advisory) wins outright. When the
 * server fell back to `notifications.view` / `notifications.inbox` — which it
 * does for every module event outside messaging and advisories — the
 * `resource` picks the page. Anything else opens the inbox, with the detail
 * sheet for the notification when its id is known.
 */
export function resolveRealtimeTarget(envelope: RealtimeEnvelope): RouteLocationRaw {
  const action = envelope.action;
  if (action && action.route !== 'notifications.view' && action.route !== 'notifications.inbox') {
    return resolveRealtimeRoute(action, envelope.resource);
  }
  return resolveResourceRoute(envelope.resource) ?? resolveRealtimeRoute(action, envelope.resource);
}
